/**
 * Contrações e concordância do português, para texto montado por interpolação.
 *
 * Um gerador escreve frases juntando pedaços: `de ${sujeito}` com
 * `sujeito = "os participantes"` produz "de os participantes". Em oito itens isso
 * já apareceu duas vezes; em oitenta apareceria em toda página. A correção não é
 * reescrever cada template com cuidado — é nunca deixar a preposição encostar no
 * artigo sem passar por aqui.
 *
 * O MESMO ARGUMENTO, UM NÍVEL ACIMA. Gênero e número têm o problema idêntico, e ele
 * custou mais caro: `Quantos ${ctx.pergunta}` compila com `pergunta = 'consultas'` e
 * imprime **"Quantos consultas escolheram Rotina?"** no comando. O banco tentava se
 * defender escrevendo concordância à mão — 67 ternários `n === 1 ? 'saco' : 'sacos'`
 * em 26 arquivos — e a função `plural()` que existia aqui foi usada **zero vezes**,
 * porque exigir as duas formas no ponto de uso não poupava trabalho nenhum.
 *
 * Por isso a API abaixo não pede as formas: pede a **chave** do substantivo em
 * `lexico.ts`, e devolve a frase já concordada.
 *
 *     `${n} ${n === 1 ? 'saco' : 'sacos'}`   →   conta(n, 'saco')
 *     `Quantos ${ctx.pergunta}`              →   quantos('consulta')   // "Quantas consultas"
 *     `cobrir ${ctx.onde} inteiro`           →   `cobrir ${oS('praca').adj('inteiro')}`
 *
 * O tipo do argumento é a união das chaves do léxico, então substantivo não declarado
 * **não compila**: quem escreve item novo declara a palavra uma vez e ganha a
 * concordância de graça. É a única forma de a Frente 2 consertar trinta defeitos sem o
 * item 43 trazer o trigésimo primeiro.
 *
 * SINTAGMA, NÃO STRING. `conta()` e companhia devolvem um objeto que **lembra** o
 * gênero e o número do que construiu, para que a palavra seguinte ainda possa
 * concordar (`.adj('inteiro')`, `.vb('ser.pres')`). Ele tem `toString()`, então
 * `${...}` numa interpolação continua funcionando como sempre.
 */

import { ADJETIVOS, NOMES, VERBOS } from './lexico';
import { lerNumeroBR } from './num';
import type { AdjetivoId, Genero, Numero, NomeId, VerboId } from './lexico';

export type { Genero, Numero, NomeId, NomeDoGenero, AdjetivoId, VerboId } from './lexico';

/** Preposições que contraem com artigo definido. */
const CONTRACOES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  de: { o: 'do', a: 'da', os: 'dos', as: 'das', um: 'dum', uma: 'duma' },
  em: { o: 'no', a: 'na', os: 'nos', as: 'nas', um: 'num', uma: 'numa' },
  por: { o: 'pelo', a: 'pela', os: 'pelos', as: 'pelas' },
  a: { o: 'ao', a: 'à', os: 'aos', as: 'às' },
};

/**
 * Junta preposição e frase, contraindo quando a frase começa por artigo definido.
 *
 *   prep('de', 'os participantes')  ->  'dos participantes'
 *   prep('de', 'uma corrida')       ->  'de uma corrida'   (duma soa arcaico)
 *   prep('em', 'a alta temporada')  ->  'na alta temporada'
 */
export function prep(preposicao: keyof typeof CONTRACOES | string, frase: Frase): string {
  const f = String(frase).trim();
  if (f === '') return preposicao;

  const tabela = CONTRACOES[preposicao];
  if (!tabela) return `${preposicao} ${f}`;

  const esp = f.indexOf(' ');
  const primeira = (esp === -1 ? f : f.slice(0, esp)).toLowerCase();
  const resto = esp === -1 ? '' : f.slice(esp + 1);

  // 'dum'/'duma' existem, mas soam arcaicos em prosa escolar: preferimos a forma solta.
  if (primeira === 'um' || primeira === 'uma') return `${preposicao} ${f}`;

  const contraida = tabela[primeira];
  if (!contraida) return `${preposicao} ${f}`;
  return resto === '' ? contraida : `${contraida} ${resto}`;
}

/** Atalho para a preposição mais frequente. */
export const de = (frase: Frase): string => prep('de', frase);
export const em = (frase: Frase): string => prep('em', frase);

/** Primeira letra maiúscula, para uma frase que passa a abrir o período. */
export function capitalizar(frase: Frase): string {
  const f = String(frase).trimStart();
  return f.length === 0 ? f : f[0]!.toUpperCase() + f.slice(1);
}

/**
 * Enumeração com vírgulas e "e" antes do último — a forma que o banco escrevia com
 * `join(', ')` e que deixou "2,5, 7/2, 3" sem conjunção no item da reta numérica.
 */
export function lista(partes: readonly Frase[], separador = ', '): string {
  const p = partes.map(String).filter((x) => x !== '');
  if (p.length === 0) return '';
  if (p.length === 1) return p[0]!;
  return `${p.slice(0, -1).join(separador)} e ${p[p.length - 1]!}`;
}

// ---------------------------------------------------------------------------
// Sintagma: texto que carrega o próprio gênero e número
// ---------------------------------------------------------------------------

/** Qualquer coisa que vira texto numa interpolação: string ou sintagma. */
export type Frase = string | Sintagma;

export interface Sintagma {
  readonly texto: string;
  readonly g: Genero;
  readonly n: Numero;
  /** Acrescenta adjetivo já concordado: `oS('praca').adj('inteiro')` → "a praça inteira". */
  adj(id: AdjetivoId): Sintagma;
  /** Acrescenta verbo já concordado: `livre('as vendas', 'f', 'pl').vb('mudar.futpret')`. */
  vb(id: VerboId): Sintagma;
  /** Acrescenta texto que não flexiona (complemento, aposto, medida). */
  mais(texto: Frase): Sintagma;
  toString(): string;
}

function sintagma(texto: string, g: Genero, n: Numero): Sintagma {
  return {
    texto,
    g,
    n,
    adj: (id) => sintagma(`${texto} ${adjetivo(id, g, n)}`, g, n),
    vb: (id) => sintagma(`${texto} ${verbo(id, n)}`, g, n),
    mais: (extra) => {
      const e = String(extra).trim();
      return e === '' ? sintagma(texto, g, n) : sintagma(`${texto} ${e}`, g, n);
    },
    toString: () => texto,
  };
}

/**
 * Texto que não está no léxico, mas cujo gênero e número o autor conhece.
 *
 * Existe porque nem toda unidade de concordância é palavra: os contextos do banco
 * guardam sintagmas inteiros (`'as vendas'`, `'o volume de água'`,
 * `'os participantes de uma corrida de rua'`), e tabelá-los seria tabelar frases. O
 * que o léxico impede é o gênero ficar **implícito**; aqui ele fica explícito.
 */
export const livre = (texto: string, g: Genero, n: Numero): Sintagma => sintagma(texto, g, n);

/**
 * Quantos são → singular ou plural. Zero e fração vão para o plural, como manda a
 * língua: "0 pontos", "1,5 graus"; menos um é singular, como um: "−1 grau".
 *
 * Exportado porque a contagem nem sempre nomeia coisas do léxico — às vezes o que se
 * conta são nomes de vértices, rótulos, colunas —, e sem isto quem precisa do número
 * escreve `n === 1 ? 'sg' : 'pl'` no lugar. Eu escrevi, dez minutos depois de instalar
 * a catraca que conta exatamente isso, e foi ela que me avisou.
 */
export const numeroDe = (n: number): Numero => (Math.abs(n) === 1 ? 'sg' : 'pl');

/** A forma do substantivo, sem artigo nem numeral. */
export function nome(id: NomeId, n: Numero = 'sg'): Sintagma {
  const w = NOMES[id];
  return sintagma(n === 'sg' ? w.sg : w.pl, w.g, n);
}

/**
 * Numeral + substantivo concordado: `conta(1, 'saco')` → "1 saco",
 * `conta(3, 'saco')` → "3 sacos", `conta(0, 'ponto')` → "0 pontos".
 *
 * O numeral entra como veio (já formatado, se for o caso), porque quem decide a
 * vírgula decimal é quem chama.
 */
export function conta(n: number | string, id: NomeId): Sintagma {
  const w = NOMES[id];
  // O numeral pode vir já escrito à moda daqui — o de `formatBR`: o ponto separa o
  // milhar, e o menos é o tipográfico. '1.000' é mil, e lê-lo como 1 devolveria
  // "1.000 livro"; '−1' é menos um, e é singular.
  const valor = typeof n === 'number' ? n : (lerNumeroBR(n) ?? NaN);
  const num = numeroDe(valor);
  return sintagma(`${n} ${num === 'sg' ? w.sg : w.pl}`, w.g, num);
}

/** Artigo definido singular: `oS('praca')` → "a praça", `oS('saco')` → "o saco". */
export function oS(id: NomeId): Sintagma {
  const w = NOMES[id];
  return sintagma(`${w.g === 'f' ? 'a' : 'o'} ${w.sg}`, w.g, 'sg');
}

/** Artigo definido plural: `osN('praca')` → "as praças". */
export function osN(id: NomeId, quantos?: number | string): Sintagma {
  const w = NOMES[id];
  if (quantos === undefined) return sintagma(`${artigo(w.g, 'pl')} ${w.pl}`, w.g, 'pl');
  // Com a contagem no meio — "as 120 consultas" —, que é onde o artigo escapava: o
  // numeral entre artigo e substantivo afasta os dois o bastante para o olho perder a
  // concordância, e o banco escrevia "os 120 consultas". Concorda com a contagem:
  // "a 1 casa", e não "as 1 casas".
  const c = conta(quantos, id);
  return sintagma(`${artigo(w.g, c.n)} ${c}`, w.g, c.n);
}

/**
 * O artigo definido sozinho, para quando o substantivo fica implícito: "não são
 * exatamente AS pedidas", "OS que faltaram". Sem ele, cada frase dessas escrevia o
 * próprio `g === 'f' ? 'as' : 'os'` — e uma delas, fixa no feminino, dizia "As pedidas
 * eram 3" sobre polígonos.
 */
export function artigo(g: Genero, n: Numero): string {
  return n === 'sg' ? (g === 'f' ? 'a' : 'o') : g === 'f' ? 'as' : 'os';
}

/** Artigo indefinido: `umA('urna')` → "uma urna", `umA('saco')` → "um saco". */
export function umA(id: NomeId): Sintagma {
  const w = NOMES[id];
  return sintagma(`${w.g === 'f' ? 'uma' : 'um'} ${w.sg}`, w.g, 'sg');
}

/**
 * O interrogativo do comando, já concordado e com maiúscula — é a frase mais visível
 * do item, e é exatamente onde saiu "Quantos consultas escolheram Rotina?".
 *
 *   quantos('consulta') → "Quantas consultas"
 *   quantos('aluno')    → "Quantos alunos"
 */
export function quantos(id: NomeId): Sintagma {
  const w = NOMES[id];
  return sintagma(`${w.g === 'f' ? 'Quantas' : 'Quantos'} ${w.pl}`, w.g, 'pl');
}

/** A forma do adjetivo para um gênero e número dados. */
export function adjetivo(id: AdjetivoId, g: Genero, n: Numero): string {
  const a = ADJETIVOS[id];
  return n === 'sg' ? (g === 'f' ? a.fs : a.ms) : g === 'f' ? a.fp : a.mp;
}

/** A forma do verbo para um número dado (só terceira pessoa; o tempo está na chave). */
export function verbo(id: VerboId, n: Numero): string {
  const v = VERBOS[id];
  return n === 'sg' ? v.sg : v.pl;
}

/**
 * Concorda com um sintagma já pronto — para quando sujeito e verbo ficam separados
 * por outras palavras e não dá para encadear `.vb()`.
 */
export const comoEm = (s: Sintagma, id: VerboId): string => verbo(id, s.n);
